import React from "react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "October 28, 2026";
const CONTACT_EMAIL = "hello@sportifide.com";
// Replace with the registered company name once Sportifide is incorporated.
const OPERATOR = "Sportifide";

export default function TermsOfService() {
  const mail = <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>;
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Link to="/login" style={styles.back}>← Back to Login</Link>
        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.meta}>Effective date: {EFFECTIVE_DATE} · Sportifide</p>

        <Section title="1. Acceptance of Terms">
          <p>Sportifide ("the Platform," "we," "us," or "our") is operated by {OPERATOR} from
          Ontario, Canada. By accessing or using the Platform, you agree to these Terms of Service
          ("Terms"). If you do not agree, do not use the Platform. If you use the Platform for a
          school, club, or other organisation, you agree to these Terms on its behalf and confirm
          you are authorised to do so.</p>
        </Section>

        <Section title="2. Who Can Use Sportifide">
          <p>You must be at least 13 years old to create your own account. If you are 13 to 17,
          a parent or legal guardian must review and agree to these Terms for you. In India, anyone
          under 18 may use the Platform only with verifiable consent from a parent or guardian.</p>
          <p>Younger students may take part only through a school or youth organisation that has
          obtained the required parent or guardian consent (see section 6). By using the Platform
          you confirm you meet these requirements.</p>
        </Section>

        <Section title="3. Your Account">
          <ul>
            <li>You are responsible for keeping your credentials confidential.</li>
            <li>You may not share your account or use another person's account.</li>
            <li>You must provide accurate information during registration.</li>
            <li>You must tell us immediately at {mail} if you suspect unauthorised access.</li>
            <li>We may suspend or terminate accounts that violate these Terms.</li>
          </ul>
        </Section>

        <Section title="4. Acceptable Use">
          <p>You agree <strong>not</strong> to:</p>
          <ul>
            <li>Post content that is abusive, harassing, defamatory, obscene, or discriminatory.</li>
            <li>Contact, harass, or collect information about minors for any purpose unrelated
            to their sports activities.</li>
            <li>Impersonate any person or entity or misrepresent your affiliation.</li>
            <li>Use the Platform for any unlawful purpose.</li>
            <li>Attempt to gain unauthorised access to any part of the Platform or its systems.</li>
            <li>Transmit spam, viruses, or any malicious code.</li>
            <li>Scrape, crawl, or data-mine the Platform without express written permission.</li>
            <li>Manipulate scores, standings, or results fraudulently.</li>
          </ul>
        </Section>

        <Section title="5. Sports Events &amp; Community Content">
          <p>Sportifide is a platform for organising and taking part in sports activities,
          leagues, tournaments, and matches. We do not organise, supervise, or verify events, venues,
          or participant details that users submit.</p>
          <p>Organisers are solely responsible for the safety and legality of the events they run
          through the Platform, including venue permits, participant waivers, first aid, and
          appropriate event or liability insurance. Players take part in sporting activities at
          their own risk. Sportifide is not liable for injuries, damages, or disputes arising from
          real-world sporting events organised through the Platform.</p>
          <p>Where an organiser collects fees from players outside the Platform, that arrangement
          is between the organiser and the players.</p>
        </Section>

        <Section title="6. Schools and Organisations">
          <p>If you set up or manage a school, club, or other organisation space, you confirm that:</p>
          <ul>
            <li>you are authorised by the organisation to do so;</li>
            <li>the organisation has a lawful basis, including any required parent or guardian
            consent, for every person's information it enters, and has told those people how it
            will be used;</li>
            <li>you will enter only the information needed for sports activities, and not
            government ID numbers, home addresses, or medical information; and</li>
            <li>you will give access only to staff, students, and parents who need it.</li>
          </ul>
          <p>The organisation owns and controls the records it enters. We process them only to
          provide the Platform to the organisation, as described in our{" "}
          <Link to="/privacy" style={styles.link}>Privacy Policy</Link>. If the organisation has a
          separate written agreement with us (for example, a pilot or subscription agreement), that
          agreement prevails over these Terms where they differ.</p>
        </Section>

        <Section title="7. Fees and Payments">
          <p>Playing on Sportifide is free. Some organiser and organisation features may be paid;
          the price, billing period, and what is included are shown before you buy or set out in
          a written quote or agreement. Prices exclude applicable taxes (such as GST/HST or Indian
          GST), which are added as the law requires.</p>
          <p>Payments are processed by our payment provider (Stripe) or paid by bank transfer
          against our invoice. Unless a written agreement says otherwise, paid plans renew only
          when you pay the next invoice, and fees already paid are not refundable except where the
          law requires.</p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>The Sportifide name, logo, and Platform are owned by {OPERATOR}. You may not use any
          Sportifide branding without prior written consent.</p>
          <p>You keep ownership of the content you submit (profile photos, team names, match data,
          and similar). You grant Sportifide a non-exclusive, worldwide, royalty-free licence to
          host, use, display, and distribute that content only to operate and improve the Platform.
          This licence ends when you delete the content or your account, except for content
          already shared with others (such as results of matches you played).</p>
        </Section>

        <Section title="9. Privacy">
          <p>Your use of the Platform is also governed by our{" "}
          <Link to="/privacy" style={styles.link}>Privacy Policy</Link>, which is part of these
          Terms.</p>
        </Section>

        <Section title="10. Disclaimers">
          <p>The Platform is provided "as is" and "as available" without warranties of any kind,
          express or implied, to the extent the law allows. We do not warrant that the Platform will
          be uninterrupted, error-free, or free of viruses, and we make no guarantees about the
          accuracy of any sports data, statistics, or results.</p>
        </Section>

        <Section title="11. Limitation of Liability">
          <p>To the fullest extent permitted by law, Sportifide and its operators are not liable
          for any indirect, incidental, special, consequential, or punitive damages arising from your
          use of or inability to use the Platform, even if advised of the possibility of such
          damages. Our total liability for any claim relating to the Platform is limited to the
          greater of the fees you paid us in the 12 months before the claim and CA$100.</p>
          <p>Nothing in these Terms limits rights you have under consumer protection laws that
          cannot be waived.</p>
        </Section>

        <Section title="12. Indemnification">
          <p>You agree to indemnify and hold harmless Sportifide and its operators from any claims,
          losses, liabilities, damages, costs, and expenses (including reasonable legal fees) arising
          from your violation of these Terms, events you organise, or information you enter about
          other people.</p>
        </Section>

        <Section title="13. Termination">
          <p>We may suspend or terminate your account for violations of these Terms, or close the
          Platform or a feature with reasonable notice. You may delete your account at any time from
          your Profile settings. Upon termination, your right to use the Platform ends; sections
          that by their nature should continue (such as 8, 10, 11, 12, and 14) continue to apply.</p>
        </Section>

        <Section title="14. Governing Law and Disputes">
          <p>These Terms are governed by the laws of the Province of Ontario and the federal laws of
          Canada that apply there. We will first try to resolve any dispute through good-faith
          discussion; if that fails, the courts of Ontario, sitting in Toronto, have exclusive
          jurisdiction, unless the law where you live gives you the right to bring a claim in your
          local courts.</p>
        </Section>

        <Section title="15. Changes to These Terms">
          <p>We may update these Terms from time to time. We will tell registered users by email or
          an in-app notice before material changes take effect. Continuing to use the Platform after
          that means you accept the updated Terms.</p>
        </Section>

        <Section title="16. Contact">
          <p>Questions about these Terms? Contact us at {mail}.</p>
        </Section>

        <footer style={styles.footer}>
          © {new Date().getFullYear()} Sportifide. All rights reserved.
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: "1.75rem" }}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      <div style={styles.body}>{children}</div>
    </section>
  );
}

const styles = {
  page: {
    position: "fixed",
    inset: 0,
    overflowY: "auto",
    overflowX: "hidden",
    background: "linear-gradient(135deg, #0f1729 0%, #1a2640 100%)",
    padding: "2rem 1rem",
    fontFamily: "Arial, sans-serif",
  },
  card: {
    maxWidth: 760,
    margin: "0 auto",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 16,
    padding: "2.5rem 2rem",
    color: "#e0e6f0",
  },
  back: {
    display: "inline-block",
    marginBottom: "1.5rem",
    color: "#fd7e14",
    textDecoration: "none",
    fontSize: "0.9rem",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#fff",
    margin: "0 0 0.25rem",
  },
  meta: {
    color: "rgba(255,255,255,0.45)",
    fontSize: "0.85rem",
    marginBottom: "2rem",
  },
  sectionTitle: {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "#fd7e14",
    marginBottom: "0.5rem",
  },
  body: {
    fontSize: "0.92rem",
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.8)",
  },
  link: { color: "#fd7e14" },
  footer: {
    marginTop: "2.5rem",
    paddingTop: "1rem",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    textAlign: "center",
    fontSize: "0.82rem",
    color: "rgba(255,255,255,0.35)",
  },
};
